import { useState, type FormEvent } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useAuthContext } from "@/hooks/useAuthContext";
import { useAuthStore } from "@/stores/auth-store";
import { STRINGS } from "@/constants/strings";
import { LoaderCircleIcon } from "lucide-react";

export function LoginForm({
  className,
  onForgotPassword,
  ...props
}: React.ComponentProps<"div"> & {
  onForgotPassword?: () => void;
}) {
  const { login } = useAuthContext();
  const error = useAuthStore((s) => s.error);
  const branding = useAuthStore((s) => s.branding);
  const tenantName = useAuthStore((s) => s.tenantName);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const email = (form.elements.namedItem("email") as HTMLInputElement).value;
    const password = (form.elements.namedItem("password") as HTMLInputElement)
      .value;

    setIsSubmitting(true);
    try {
      await login(email, password);
    } catch {
      // error is already set in the store by AuthProvider
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <form onSubmit={handleSubmit}>
        <FieldGroup>
          <div className="flex flex-col items-center gap-2 text-center">
            {branding?.logoUrl && (
              <img
                src={branding.logoUrl}
                alt={tenantName ?? STRINGS.app.name}
                className="h-10 w-auto"
              />
            )}
            <h1 className="text-xl font-bold">
              {tenantName ?? STRINGS.app.name}
            </h1>
            <FieldDescription>
              {STRINGS.auth.loginSubtitle}
            </FieldDescription>
          </div>

          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <Field>
            <FieldLabel htmlFor="email">{STRINGS.auth.emailLabel}</FieldLabel>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder={STRINGS.auth.emailPlaceholder}
              autoComplete="email"
              required
              disabled={isSubmitting}
            />
          </Field>

          <Field>
            <div className="flex items-center justify-between">
              <FieldLabel htmlFor="password">
                {STRINGS.auth.passwordLabel}
              </FieldLabel>
              {onForgotPassword && (
                <button
                  type="button"
                  onClick={onForgotPassword}
                  className="text-xs text-muted-foreground underline-offset-4 hover:underline"
                >
                  {STRINGS.auth.forgotPasswordLink}
                </button>
              )}
            </div>
            <Input
              id="password"
              name="password"
              type="password"
              placeholder={STRINGS.auth.passwordPlaceholder}
              autoComplete="current-password"
              required
              disabled={isSubmitting}
            />
          </Field>

          <Field>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <LoaderCircleIcon className="animate-spin" />
                  {STRINGS.auth.loggingIn}
                </>
              ) : (
                STRINGS.auth.loginButton
              )}
            </Button>
          </Field>
        </FieldGroup>
      </form>
    </div>
  );
}
